import { AuthenticationFormProps } from '../AuthenticationForm.types'
import { Box, Button, Skeleton, Stack } from '@dtx-company/design-system/src'
import { FC, useCallback } from 'react'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { matchesRoute } from '@dtx-company/true-common/src/utils/urls/matchesRoute'
import { SocialProvider } from '@dtx-company/true-common/src/types/socialProvider'
import {
  auth_signin_form_facebook_started,
  auth_signin_form_google_started,
  auth_signup_form_facebook_started,
  auth_signup_form_google_started
} from '@dtx-company/inter-app/src/constants/authHeapEvents'
import { fireAnalyticsEvent } from '../../../event-tracking/helpers/fireAnalyticsEvent'
import { useAuthCache } from '@dtx-company/app/shared/authentication/AuthCache/hooks'
import { useAuthFormContext } from '../AuthenticationForm'
import { useCodeGenerator } from '../AuthenticationForm.hooks'
import {
  useFacebookLoginUrl,
  useGoogleLoginUrl
} from '@dtx-company/app/shared/common/src/hooks/useSocialLoginUrl'
import { usePathname } from 'next/navigation'
import { useRouter } from 'next/router'
import Image from 'next/image'
import useTranslation from 'next-translate/useTranslation'

export const SocialLoginButtons: FC<
  {
    checkTerms: (args: { quiet?: boolean } | void) => boolean
    fireHeapEventOnClick?: (provider: SocialProvider) => void
    validateOnFormFocus?: () => boolean
    onSubmitError?: () => void
  } & Pick<AuthenticationFormProps, 'onSubmit'>
> = ({ checkTerms, fireHeapEventOnClick, validateOnFormFocus, onSubmitError }) => {
  const { setLocgFormValuesInAuthCache, handleGeneratorCodeCreation } = useCodeGenerator()

  const isSignUp = useAuthFormContext().action === 'sign up'
  const { t } = useTranslation('authentication')
  const pathname = usePathname() ?? ''
  const router = useRouter()
  const isFreeQrGeneratorPage = matchesRoute(pathname, Routes.FREE_QR_CODE_GENERATOR)
  const queryParams = router.query
  const authCache = useAuthCache()

  const trackSignupButtonClicked = useCallback((provider: SocialProvider) => {
    fireAnalyticsEvent('Revenue_SignUpPage_Clicked_SignUpButton', {
      signupButtonType: provider.toUpperCase()
    })
    fireAnalyticsEvent('userClickedAuthFormAuthenticateWithSocial', {
      socialProvider: provider
    })
  }, [])

  const { url: googleLoginUrl } = useGoogleLoginUrl(isSignUp ? 'signup' : 'login')
  const { url: facebookLoginUrl } = useFacebookLoginUrl('login')

  const handleAuthClick = async (provider: SocialProvider): Promise<void> => {
    trackSignupButtonClicked(provider)
    if (!checkTerms()) return
    if (validateOnFormFocus ? !validateOnFormFocus() : false) {
      onSubmitError?.()
      return
    }
    fireHeapEventOnClick?.(provider)

    if (isFreeQrGeneratorPage) {
      setLocgFormValuesInAuthCache()
      await handleGeneratorCodeCreation()
    }

    authCache.queryParams.set(queryParams)

    switch (provider) {
      case SocialProvider.GOOGLE:
        window.location.href = googleLoginUrl
        break
      case SocialProvider.FACEBOOK:
        window.location.href = facebookLoginUrl
        break
    }
  }

  const disabled = !checkTerms({ quiet: true })

  return (
    <Stack
      sx={{
        gap: 4,
        '& .MuiBox-root': {
          display: 'flex',
          alignItems: 'center'
        }
      }}
    >
      {googleLoginUrl ? (
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          fullWidth
          onClick={() => {
            handleAuthClick(SocialProvider.GOOGLE)
            if (!disabled) {
              fireAnalyticsEvent(
                isSignUp ? auth_signup_form_google_started : auth_signin_form_google_started
              )
            }
          }}
        >
          <Box sx={{ pr: 6 }}>
            <Image
              src={'/icons/social-media/google-color.svg'}
              alt=""
              role="presentation"
              width={18}
              height={18}
            />
          </Box>
          {t('SocialsLoginButton.label', {
            action: isSignUp ? 'Sign up' : 'Sign in',
            social: 'Google'
          })}
        </Button>
      ) : (
        <Skeleton variant="rectangular" height={50} />
      )}

      {!isFreeQrGeneratorPage && (
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          fullWidth
          onClick={() => {
            handleAuthClick(SocialProvider.FACEBOOK)
            if (!disabled) {
              fireAnalyticsEvent(
                isSignUp ? auth_signup_form_facebook_started : auth_signin_form_facebook_started
              )
            }
          }}
        >
          <Box sx={{ pr: 6 }}>
            <Image
              src={'/icons/social-media/facebook-color.svg'}
              alt=""
              role="presentation"
              width={18}
              height={18}
            />
          </Box>
          {t('SocialsLoginButton.label', {
            action: isSignUp ? 'Sign up' : 'Sign in',
            social: 'Facebook'
          })}
        </Button>
      )}
    </Stack>
  )
}
